import React, { useEffect, useState, Fragment } from 'react'
import { Grid, SnackbarContent, IconButton, Box, Collapse, useTheme } from '@mui/material'
import { Close, Warning, Info, Error, CheckCircle } from '@mui/icons-material'
import { SnackbarProps, messageVariants } from '../../types/default-types'
import { withPlanner, WithPlannerProps } from '../hoc'
import { str } from '../../utils'

type VariantProps = {
  variant: messageVariants
  message: string
}

export const Snackbar: React.FC<SnackbarProps & WithPlannerProps> = withPlanner(
  ({ variantMessage, message, iconVisible = true, onClose, planner, ...props }): JSX.Element => {
    const {
      palette: { success, warning, error, info },
    } = useTheme()
    const variant: messageVariants = message ? variantMessage : 'reset'
    const [variantState, setVariantState] = useState<VariantProps>({
      variant: 'reset',
      message: '',
    })

    const variantColor = { success, warning, error, info, reset: { main: 'white' } }
    const variantIcon = {
      success: CheckCircle,
      warning: Warning,
      error: Error,
      info: Info,
      reset: undefined,
    }

    const Icon = variantIcon[variantState.variant]
    const [animClass, setAnimClass] = useState<'zoomIn' | 'zoomOut'>('zoomIn')
    const [open, setOpen] = useState<boolean>(false)

    const handleClose = () => {
      setAnimClass('zoomOut')
      planner?.timeout(() => {
        setOpen(false)
        if (onClose) onClose()
      }, 150)
    }

    useEffect(() => {
      if (message && planner?.clearTimeouts()) {
        setVariantState({
          variant,
          message,
        })
        planner?.timeout(() => {
          setAnimClass('zoomIn')
          setOpen(true)
        }, 150)
      } else handleClose()
    }, [message])

    return (
      <Collapse in={open}>
        <Box className={`animated ${animClass} faster`}>
          <SnackbarContent
            aria-describedby="client-snackbar"
            message={
              <Box id="client-snackbar" color={variantColor[variantState.variant]?.main}>
                <Grid container justifyContent="space-between" wrap="nowrap" alignItems="center">
                  <Grid item xs={12}>
                    <Grid container justifyContent="center">
                      <Grid item>
                        {!!variantState.message &&
                          Object.entries(variantState.message).map(([_, val], idx) => (
                            <Box key={`snackbar-message-${idx}`}>
                              {Array.isArray(val) ? (
                                val.map((v, i) => <Fragment key={i}>{str.normalize(v)}</Fragment>)
                              ) : (
                                <>{str.normalize(val)}</>
                              )}
                            </Box>
                          ))}
                      </Grid>
                    </Grid>
                  </Grid>

                  {iconVisible && (
                    <Grid item>
                      <IconButton
                        key="close"
                        color="error"
                        aria-label="close"
                        onClick={handleClose}
                        style={{ marginRight: -20 }}
                      >
                        <Close style={{ fontSize: 22 }} />
                      </IconButton>
                    </Grid>
                  )}
                </Grid>
              </Box>
            }
            {...props}
          />
        </Box>
      </Collapse>
    )
  }
)
